import { useState } from 'react';
import {
	Alert,
	Button,
	Checkbox,
	Collapse,
	Flex,
	Input,
	Modal,
	Radio,
	Select,
	Space,
	Table,
	Tag,
	Typography,
	message,
} from 'antd';
import type {
	AuditDocumentListItem,
	AuditStatus,
	CompletionDateMode,
	RecoveryPreviewResult,
} from '../../redux/api/traveler-audit';
import {
	useApproveDocumentsMutation,
	useCompletePriorTasksMutation,
	useLazyGetMatchingDocumentIdsQuery,
	usePrintCompletedDocumentsMutation,
	useSetPrintStatusMutation,
	usePreviewRecoveryMutation,
	useRecoverDocumentsMutation,
	useReturnToReviewMutation,
	useRollbackDocumentsMutation,
} from '../../redux/api/traveler-audit';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import { ManualRecoveryModal } from './ManualRecoveryModal';
import { fieldLabel, formatAuditValue, methodLabel } from './recoveryLabels';
import {
	type ApproveRow,
	RECOVERABLE,
	isRecoverable,
	splitPriorTaskRefusals,
	splitPriorTaskResults,
} from './reviewRows';

const { Text } = Typography;

const formatStep = (user?: string | null, at?: string | null) => {
	if (!user && !at) return '—';
	const date = at ? at.slice(0, 10) : '—';
	return `${user || '—'} · ${date}`;
};

export function OverlayDates({ doc }: { doc: AuditDocumentListItem }) {
	const recovered = new Set(doc.header.recovered_fields || []);
	const highlight = (key: string) =>
		recovered.has(key)
			? { background: '#fff7e6', padding: '0 4px', borderRadius: 4 }
			: undefined;
	return (
		<Space size={4} wrap>
			<span style={highlight('order_accepted')}>
				OA{' '}
				{formatStep(
					doc.header.order_accepted?.user,
					doc.header.order_accepted?.at
				)}
			</span>
			<span style={highlight('icf_operator')}>
				ICF{' '}
				{formatStep(doc.header.icf_operator?.user, doc.header.icf_operator?.at)}
			</span>
			<span style={highlight('machining_complete')}>
				MC{' '}
				{formatStep(
					doc.header.machining_complete?.user,
					doc.header.machining_complete?.at
				)}
			</span>
		</Space>
	);
}

export type ReviewDoneOptions = { revealRecovered?: boolean };

type Props = {
	runId: string;
	selected: AuditDocumentListItem[];
	statusFilter: AuditStatus | 'all';
	lookoutsOnly: boolean;
	failuresOnly: boolean;
	search: string;
	issueCategories: string[];
	onDone: (opts?: ReviewDoneOptions) => void;
};

const statusColor = (status?: string | null) => {
	if (status === 'pass') return 'green';
	if (status === 'review') return 'gold';
	if (status === 'fail') return 'red';
	if (status === 'complete') return 'blue';
	if (status === 'printed') return 'purple';
	return 'default';
};

const rowSummary = (row: RecoveryPreviewResult) => {
	if (row.ok === false) return row.error || 'Apply failed';
	if (row.status === 'fail') {
		return `still failing: ${row.failures?.[0] || 'see details'}`;
	}
	const n = row.events?.length || 0;
	return n ? `${n} field${n === 1 ? '' : 's'} updated` : 'No fields changed';
};

export function ReviewPanel({
	runId,
	selected,
	statusFilter,
	lookoutsOnly,
	failuresOnly,
	search,
	issueCategories,
	onDone,
}: Props) {
	const [approveOpen, setApproveOpen] = useState(false);
	const [recoverOpen, setRecoverOpen] = useState(false);
	const [reportOpen, setReportOpen] = useState(false);
	const [reportRows, setReportRows] = useState<RecoveryPreviewResult[]>([]);
	const [manualId, setManualId] = useState<string | null>(null);
	const [mode, setMode] = useState<CompletionDateMode>('today');
	const [certNa, setCertNa] = useState(false);
	const [revision, setRevision] = useState('');
	const [keyenceId, setKeyenceId] = useState<string>();
	const [keyenceOperator, setKeyenceOperator] = useState('');
	const [keyenceAt, setKeyenceAt] = useState('');
	const [selectAllMatching, setSelectAllMatching] = useState(false);
	const [previewError, setPreviewError] = useState<string | null>(null);
	const [recoverIds, setRecoverIds] = useState<string[] | null>(null);
	const [priorRows, setPriorRows] = useState<ApproveRow[]>([]);

	const [loadMatching] = useLazyGetMatchingDocumentIdsQuery();
	const [approve, { isLoading: isApproving }] = useApproveDocumentsMutation();
	const [printCompleted, { isLoading: isPrintingCompleted }] =
		usePrintCompletedDocumentsMutation();
	const [setPrintStatus, { isLoading: isSettingPrintStatus }] =
		useSetPrintStatusMutation();
	const [preview, { data: previewData, isLoading: isPreviewing }] =
		usePreviewRecoveryMutation();
	const [recover, { isLoading: isRecovering }] = useRecoverDocumentsMutation();
	const [rollback, { isLoading: isRollingBack }] =
		useRollbackDocumentsMutation();
	const [returnToReview, { isLoading: isReturning }] =
		useReturnToReviewMutation();
	const [completePriorTasks, { isLoading: isCompletingTasks }] =
		useCompletePriorTasksMutation();

	const selectedIds = selected.map((d) => d.id);
	const passReview = selected.filter(
		(d) => d.status === 'pass' || d.status === 'review'
	);
	const completeRows = selected.filter((d) => d.status === 'complete');
	const printedRows = selected.filter((d) => d.status === 'printed');
	const recoverableRows = selected.filter((d) => isRecoverable(d.status));
	const completedSelection = completeRows.length + printedRows.length > 0;
	const tableFilters = {
		search,
		issueCategories,
		lookoutsOnly,
		failuresOnly,
	};
	const approveStatuses = (): AuditStatus[] => {
		if (statusFilter === 'pass' || statusFilter === 'review')
			return [statusFilter];
		if (statusFilter === 'all') return ['pass', 'review'];
		return [];
	};
	const resolveIds = async (statuses?: AuditStatus[]) => {
		if (selectAllMatching) {
			const result = await loadMatching({
				runId,
				statuses,
				...tableFilters,
			}).unwrap();
			return result.ids;
		}
		return selectedIds;
	};

	const rowLabel = (row: {
		filename?: string | null;
		id?: string;
		error?: string | null;
	}) => row.filename || row.id || 'packet';

	const toastRowErrors = (
		results: Array<{
			ok?: boolean;
			filename?: string | null;
			id?: string;
			error?: string | null;
		}>,
		allFailedFallback: string
	) => {
		const bad = results.filter((r) => r.ok === false);
		if (!bad.length) return;
		const detail = bad
			.slice(0, 3)
			.map((r) => `${rowLabel(r)}: ${r.error || 'failed'}`)
			.join('; ');
		const extra = bad.length > 3 ? ` (+${bad.length - 3} more)` : '';
		message.warning(`${detail}${extra}`);
		if (bad.length === results.length) {
			message.error(allFailedFallback);
		}
	};

	const handleApprove = async () => {
		const statuses = approveStatuses();
		if (!statuses.length) {
			message.warning('Those packets still have failures. Run recovery first.');
			return;
		}
		const completeIds = selectAllMatching
			? await resolveIds(statuses)
			: passReview.map((d) => d.id);
		if (!completeIds.length) {
			message.warning('Those packets still have failures. Run recovery first.');
			return;
		}
		try {
			const result = await approve({
				runId,
				documentIds: completeIds,
				selectAllMatching,
				statuses,
				mode,
				...tableFilters,
			}).unwrap();
			const ok = result.results.filter((r) => r.ok).length;
			if (ok) {
				const jobIds = result.print_job_ids ?? [];
				const failedIds = result.print_failed_ids ?? [];
				if (!jobIds.length) {
					message.error(
						'Completed, but no print job was archived — open Print queue and retry.'
					);
				} else if (failedIds.length) {
					const next = result.print_errors?.find((row) => row.next_step)
						?.next_step;
					message.error(
						[
							`Completed ${ok} packet(s), but ${failedIds.length} print job(s) failed to build — open Print queue and retry.`,
							next,
						]
							.filter(Boolean)
							.join(' ')
					);
				} else {
					message.success(
						`Completed ${ok} packet(s). ${jobIds.length} print job(s) queued.`
					);
				}
			}
			const { prior, other } = splitPriorTaskRefusals(result.results);
			toastRowErrors(other, 'Could not complete those packets.');
			if (prior.length) {
				setPriorRows(prior);
				setApproveOpen(false);
			}
			if (ok) {
				setApproveOpen(false);
				onDone();
			}
		} catch (err) {
			message.error(
				getApiErrorMessage(err, 'Could not complete or print those packets.')
			);
		}
	};

	const handleCompletePriorTasks = async () => {
		try {
			const result = await completePriorTasks({
				runId,
				documentIds: priorRows.map((r) => r.id),
			}).unwrap();
			const rows = result.results || [];
			const { done, ready, needsRecovery } = splitPriorTaskResults(rows);
			toastRowErrors(rows, 'Could not mark prior tasks Completed.');
			if (!done.length) return;
			setPriorRows([]);
			onDone();
			if (ready) {
				message.success(
					`Prior tasks completed. ${ready} packet(s) ready to Complete.`
				);
			}
			if (needsRecovery.length) {
				await openRecover(needsRecovery);
			}
		} catch (err) {
			message.error(
				getApiErrorMessage(err, 'Could not mark prior tasks Completed.')
			);
		}
	};

	const handlePrintCompleted = async () => {
		try {
			const ids = selectAllMatching
				? await resolveIds(['complete'])
				: completeRows.map((d) => d.id);
			if (!ids.length) {
				message.warning('No completed packets to print.');
				return;
			}
			const result = await printCompleted({
				runId,
				documentIds: ids,
				selectAllMatching,
				...tableFilters,
			}).unwrap();
			const jobIds = result.print_job_ids ?? [];
			const failedIds = result.print_failed_ids ?? [];
			if (!jobIds.length) {
				message.error(
					'No print job was archived — open Print queue and retry.'
				);
			} else if (failedIds.length) {
				const next = result.print_errors?.find((row) => row.next_step)
					?.next_step;
				message.error(
					[
						`${failedIds.length} print job(s) failed to build — open Print queue and retry.`,
						next,
					]
						.filter(Boolean)
						.join(' ')
				);
			} else {
				message.success(`${jobIds.length} print job(s) queued.`);
			}
			onDone();
		} catch (err) {
			message.error(
				getApiErrorMessage(err, 'Could not queue those packets for print.')
			);
		}
	};

	const handleSetPrintStatus = async (next: 'complete' | 'printed') => {
		const rows = next === 'printed' ? completeRows : printedRows;
		if (!rows.length) {
			message.warning(
				next === 'printed'
					? 'Select completed packets to mark Printed.'
					: 'Select printed packets to return to Complete.'
			);
			return;
		}
		let ok = 0;
		const bad: string[] = [];
		for (const row of rows) {
			try {
				await setPrintStatus({
					runId,
					documentId: row.id,
					status: next,
				}).unwrap();
				ok += 1;
			} catch (err) {
				bad.push(
					`${row.filename || row.id}: ${getApiErrorMessage(err, 'failed')}`
				);
			}
		}
		if (ok) {
			message.success(
				next === 'printed'
					? `Marked ${ok} Printed.`
					: `Returned ${ok} to Complete for reprint.`
			);
			onDone();
		}
		if (bad.length) {
			const detail = bad.slice(0, 3).join('; ');
			const extra = bad.length > 3 ? ` (+${bad.length - 3} more)` : '';
			message.warning(`${detail}${extra}`);
			if (!ok) {
				message.error('Could not update print status.');
			}
		}
	};

	const recoverTargetIds = async () =>
		selectAllMatching
			? (
					await loadMatching({
						runId,
						statuses: RECOVERABLE,
						...tableFilters,
					}).unwrap()
			  ).ids
			: recoverableRows.map((d) => d.id);

	const openRecover = async (overrideIds?: string[]) => {
		setPreviewError(null);
		try {
			const ids = overrideIds ?? (await recoverTargetIds());
			if (!ids.length) {
				message.warning('Select fail or review rows first.');
				return;
			}
			setRecoverIds(overrideIds ?? null);
			setRecoverOpen(true);
			const result = await preview({
				runId,
				documentIds: ids,
				options: { material_cert_na: certNa, revision },
			}).unwrap();
			const bad = (result.results || []).filter((r) => r.ok === false);
			if (bad.length) {
				setPreviewError(
					bad
						.slice(0, 3)
						.map((r) => `${rowLabel(r)}: ${r.error || 'preview failed'}`)
						.join('; ')
				);
			}
		} catch (err) {
			const msg = getApiErrorMessage(err, 'Recovery preview failed.');
			setPreviewError(msg);
			setRecoverOpen(true);
			message.error(msg);
		}
	};

	const handleRecover = async () => {
		try {
			const ids = recoverIds ?? (await recoverTargetIds());
			const result = await recover({
				runId,
				documentIds: ids,
				selectAllMatching: recoverIds ? false : selectAllMatching,
				...tableFilters,
				options: {
					material_cert_na: certNa,
					revision: revision || undefined,
					...(ids.length === 1
						? {
								keyence_report_id: keyenceId,
								keyence_operator: keyenceOperator || undefined,
								keyence_at: keyenceAt || undefined,
						  }
						: {}),
				},
			}).unwrap();
			const rows = result.results || [];
			if (!rows.length) {
				message.warning('No packets were recovered.');
				return;
			}
			setRecoverOpen(false);
			setReportRows(rows);
			setReportOpen(true);
		} catch (err) {
			message.error(getApiErrorMessage(err, 'Recovery failed.'));
		}
	};

	const closeReport = () => {
		setReportOpen(false);
		setReportRows([]);
		onDone({ revealRecovered: true });
	};

	const handleRollback = () => {
		Modal.confirm({
			title: 'Rollback selected packets?',
			content:
				'Restore the original job copy. Quality writes (hand-check / Keyence) stay.',
			onOk: async () => {
				try {
					const result = await rollback({
						runId,
						documentIds: selectedIds,
					}).unwrap();
					const rows = result.results || [];
					const ok = rows.filter((r) => r.ok !== false);
					const bad = rows.filter((r) => r.ok === false);
					if (ok.length) {
						message.success('Rolled back to the original packet copy.');
					}
					if (bad.length) {
						toastRowErrors(rows, 'Rollback failed for every selected packet.');
					}
					if (ok.length) onDone();
				} catch (err) {
					message.error(getApiErrorMessage(err, 'Rollback failed.'));
					return Promise.reject(err);
				}
			},
		});
	};

	const handleReturn = () => {
		Modal.confirm({
			title: 'Return completed packets to review?',
			content:
				'Sets status to Delivered In Full and clears Traveler Completed. The packet layer is kept.',
			onOk: async () => {
				try {
					const result = await returnToReview({
						runId,
						documentIds: selectedIds,
					}).unwrap();
					const rows = result.results || [];
					const ok = rows.filter((r) => r.ok !== false);
					const bad = rows.filter((r) => r.ok === false);
					if (ok.length) {
						message.success('Returned to the review queue.');
					}
					if (bad.length) {
						toastRowErrors(
							rows,
							'Return to review failed for every selected packet.'
						);
					}
					if (ok.length) onDone();
				} catch (err) {
					message.error(getApiErrorMessage(err, 'Return to review failed.'));
					return Promise.reject(err);
				}
			},
		});
	};

	const previewRows = previewData?.results ?? [];
	const keyenceReports = previewRows.flatMap((r) =>
		(r.prompts || []).flatMap((p) => p.reports || [])
	);

	const applied = reportRows.filter((r) => r.ok !== false).length;
	const nowPass = reportRows.filter(
		(r) => r.ok !== false && r.status === 'pass'
	).length;
	const nowReview = reportRows.filter(
		(r) => r.ok !== false && r.status === 'review'
	).length;
	const stillFail = reportRows.filter(
		(r) => r.ok !== false && r.status === 'fail'
	).length;
	const applyErrors = reportRows.filter((r) => r.ok === false).length;

	return (
		<>
			<Flex gap="small" wrap="wrap" align="center" style={{ marginTop: 12 }}>
				<Checkbox
					checked={selectAllMatching}
					onChange={(e) => setSelectAllMatching(e.target.checked)}
				>
					Select all matching filters
				</Checkbox>
				<Button
					type="primary"
					disabled={!selectAllMatching && passReview.length === 0}
					onClick={() => setApproveOpen(true)}
				>
					Ready to Complete (
					{selectAllMatching ? 'matching' : passReview.length})
				</Button>
				<Button
					disabled={!selectAllMatching && completeRows.length === 0}
					onClick={() => void handlePrintCompleted()}
					loading={isPrintingCompleted}
				>
					Print completed (
					{selectAllMatching ? 'matching' : completeRows.length})
				</Button>
				<Button
					disabled={completeRows.length === 0}
					onClick={() => void handleSetPrintStatus('printed')}
					loading={isSettingPrintStatus}
				>
					Mark Printed
				</Button>
				<Button
					disabled={printedRows.length === 0}
					onClick={() => void handleSetPrintStatus('complete')}
					loading={isSettingPrintStatus}
				>
					Mark Complete
				</Button>
				<Button
					disabled={!selectAllMatching && recoverableRows.length === 0}
					onClick={() => void openRecover()}
				>
					Run recovery (
					{selectAllMatching ? 'matching fail/review' : recoverableRows.length})
				</Button>
				<Button
					disabled={selectedIds.length === 0 || completedSelection}
					onClick={handleRollback}
					loading={isRollingBack}
				>
					Rollback
				</Button>
				<Button
					disabled={selectedIds.length === 0}
					onClick={handleReturn}
					loading={isReturning}
				>
					Return to review
				</Button>
				<Button
					disabled={selected.length !== 1}
					onClick={() => setManualId(selected[0].id)}
				>
					Manual recovery
				</Button>
				<Text type="secondary">{selected.length} selected on this page</Text>
			</Flex>

			<Modal
				title="Complete packets"
				open={approveOpen}
				onCancel={() => setApproveOpen(false)}
				onOk={() => void handleApprove()}
				confirmLoading={isApproving}
				okText="Mark Complete and Print"
			>
				<Radio.Group value={mode} onChange={(e) => setMode(e.target.value)}>
					<Space direction="vertical">
						<Radio value="today">Today (Central)</Radio>
					</Space>
				</Radio.Group>
				<Collapse
					style={{ marginTop: 12 }}
					items={[
						{
							key: 'advanced',
							label: 'Advanced completion date',
							children: (
								<Radio.Group
									value={mode}
									onChange={(e) => setMode(e.target.value)}
								>
									<Space direction="vertical">
										<Radio value="pb_intake">Date of PartsBadger Intake</Radio>
										<Radio value="next_business_after_intake">
											First business day after PB Intake
										</Radio>
									</Space>
								</Radio.Group>
							),
						},
					]}
				/>
				<Alert
					style={{ marginTop: 12 }}
					type="info"
					showIcon
					message="Fail packets are not completed. Run recovery first, then use this step. Review packets can run recovery first or be completed as-is. If the chosen date is before ICF Operator, it is clamped to ICF Operator."
				/>
			</Modal>

			<Modal
				title="Prior job tasks are not Completed"
				open={priorRows.length > 0}
				centered
				width={640}
				onCancel={() => setPriorRows([])}
				styles={{
					content: {
						display: 'flex',
						flexDirection: 'column',
						maxHeight: 'calc(100vh - 32px)',
					},
					body: {
						flex: '1 1 auto',
						minHeight: 0,
						overflowY: 'auto',
						overflowX: 'clip',
					},
				}}
				footer={
					<Space>
						<Button
							onClick={() => setPriorRows([])}
							disabled={isCompletingTasks}
						>
							Cancel
						</Button>
						<Button
							type="primary"
							loading={isCompletingTasks}
							onClick={() => void handleCompletePriorTasks()}
						>
							Mark prior tasks Completed
						</Button>
					</Space>
				}
			>
				<Space direction="vertical" style={{ width: '100%' }} size="middle">
					<Alert
						type="warning"
						showIcon
						message="This sets the listed tasks to Completed on the real line item. The scheduler and Kit Tracking will see them as completed. Each packet is then re-checked; any that still fail or need review open in Run recovery."
					/>
					<Table
						size="small"
						pagination={false}
						rowKey="id"
						dataSource={priorRows}
						columns={[
							{
								title: 'Packet',
								ellipsis: true,
								render: (_v, row) => rowLabel(row),
							},
							{
								title: 'Tasks to complete',
								render: (_v, row) =>
									(row.incomplete_tasks || []).join(', ') || '—',
							},
						]}
					/>
				</Space>
			</Modal>

			<Modal
				title="Run recovery rules"
				open={recoverOpen}
				onCancel={() => setRecoverOpen(false)}
				onOk={() => void handleRecover()}
				confirmLoading={isRecovering}
				width={760}
				okText="Apply recovery"
			>
				<Space direction="vertical" style={{ width: '100%' }} size="middle">
					<Checkbox
						checked={certNa}
						onChange={(e) => setCertNa(e.target.checked)}
					>
						Mark material cert N/A on this line item
					</Checkbox>
					<Input
						placeholder="Revision (if extract fails)"
						value={revision}
						onChange={(e) => setRevision(e.target.value)}
					/>
					{keyenceReports.length > 0 && (
						<>
							<Text strong>Keyence report (missing link)</Text>
							<Select
								style={{ width: '100%' }}
								placeholder="Pick a report"
								value={keyenceId}
								onChange={setKeyenceId}
								options={keyenceReports.map((r) => ({
									value: r.id,
									label: `${r.view || 'report'} · ${
										r.measurement_time || r.created_time || r.id
									}`,
								}))}
							/>
							<Input
								placeholder="Operator initials"
								value={keyenceOperator}
								onChange={(e) => setKeyenceOperator(e.target.value)}
							/>
							<Input
								placeholder="Date (ISO)"
								value={keyenceAt}
								onChange={(e) => setKeyenceAt(e.target.value)}
							/>
						</>
					)}
					{isPreviewing && (
						<Text type="secondary">Building cascade preview…</Text>
					)}
					{previewError && (
						<Alert type="error" showIcon message={previewError} />
					)}
					<Table
						size="small"
						pagination={false}
						rowKey="id"
						dataSource={previewRows}
						columns={[
							{
								title: 'Packet',
								ellipsis: true,
								width: 160,
								render: (_v, row) => row.filename || row.id,
							},
							{
								title: 'After',
								dataIndex: 'status',
								width: 80,
							},
							{
								title: 'Proposed recoveries',
								render: (_v, row) =>
									row.error
										? row.error
										: row.events?.length
										? row.events
												.map(
													(e) =>
														`${fieldLabel(e.field_key)} ← ${methodLabel(
															e.method
														)}`
												)
												.join('; ')
										: '—',
							},
						]}
					/>
				</Space>
			</Modal>

			<Modal
				title="Recovery report"
				open={reportOpen}
				onCancel={closeReport}
				onOk={closeReport}
				okText="Close"
				cancelButtonProps={{ style: { display: 'none' } }}
				width={820}
			>
				<Space wrap style={{ marginBottom: 12 }}>
					<Tag>Applied: {applied}</Tag>
					<Tag color="green">Pass: {nowPass}</Tag>
					<Tag color="gold">Review: {nowReview}</Tag>
					<Tag color="red">Still fail: {stillFail}</Tag>
					<Tag color={applyErrors ? 'red' : 'default'}>
						Apply errors: {applyErrors}
					</Tag>
				</Space>
				<Collapse
					items={reportRows.map((row) => ({
						key: row.id,
						label: (
							<Flex
								justify="space-between"
								gap={8}
								wrap="wrap"
								style={{ width: '100%' }}
							>
								<Text>{row.filename || row.id}</Text>
								<Space size={6}>
									<Tag color={statusColor(row.status)}>{row.status || '—'}</Tag>
									<Text type="secondary">{rowSummary(row)}</Text>
								</Space>
							</Flex>
						),
						children: (
							<Space
								direction="vertical"
								style={{ width: '100%' }}
								size="small"
							>
								{row.error && (
									<Alert type="error" showIcon message={row.error} />
								)}
								{(row.events || []).length > 0 && (
									<Table
										size="small"
										pagination={false}
										rowKey={(_, i) => `${row.id}-${i}`}
										dataSource={row.events}
										columns={[
											{
												title: 'Field',
												render: (_v, ev) => fieldLabel(ev.field_key),
											},
											{
												title: 'Action',
												render: (_v, ev) => methodLabel(ev.method),
											},
											{
												title: 'Before',
												render: (_v, ev) => formatAuditValue(ev.before),
											},
											{
												title: 'After',
												render: (_v, ev) => formatAuditValue(ev.after),
											},
										]}
									/>
								)}
								{!!row.failures?.length && (
									<div>
										<Text strong>Remaining failures</Text>
										<ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
											{row.failures.map((f) => (
												<li key={f}>{f}</li>
											))}
										</ul>
									</div>
								)}
								{!!row.lookouts?.length && (
									<div>
										<Text strong>Lookouts</Text>
										<ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
											{row.lookouts.map((f) => (
												<li key={f}>{f}</li>
											))}
										</ul>
									</div>
								)}
								{row.ok !== false &&
									!row.events?.length &&
									!row.failures?.length &&
									!row.error && <Text type="secondary">Nothing changed.</Text>}
							</Space>
						),
					}))}
				/>
			</Modal>

			<ManualRecoveryModal
				runId={runId}
				documentId={manualId}
				open={manualId !== null}
				onClose={(saved) => {
					setManualId(null);
					if (saved) onDone();
				}}
			/>
		</>
	);
}
